import React from 'react';
import { PersonSimpleWalk, Armchair, MoonStars, Hourglass, WifiSlash, Question } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { useRelativeTime } from '../../lib/time';
import { combinedState, latestIso } from '../../lib/movement';
import { MV, HATCH, viewTone, isNightAt } from '../../lib/movementView';

const durText = (t, min) => (min >= 60
  ? t('rep_dur_h_min', { h: Math.floor(min / 60), m: String(min % 60).padStart(2, '0') })
  : t('rep_dur_min', { m: min }));

// Big coloured light + one plain sentence. kind='room' words it for a place, not a person.
export function StatusLight({ devices, name, kind = 'patient', policy, compact = false, testid = 'movement-status-light' }) {
  const { t } = useI18n();
  const rel = useRelativeTime();
  const tone = viewTone(combinedState(devices));
  const lastMotion = latestIso(devices.map((d) => d.last_motion_at));
  const lastSignal = latestIso(devices.map((d) => d.last_sample_at || d.device_last_seen_at));
  const night = policy ? isNightAt(Date.now(), policy) : false;
  const room = kind === 'room';

  const view = {
    moving: { c: MV.moving, Icon: PersonSimpleWalk, title: t(room ? 'mvs_room_moving' : 'mvs_moving', { name }), sub: t('mvs_moving_sub', { when: rel(lastMotion) }) },
    rest: { c: MV.rest, Icon: night ? MoonStars : Armchair, title: t(room ? 'mvs_room_rest' : 'mvs_rest', { name }), sub: lastMotion ? t('mvs_rest_sub', { when: rel(lastMotion) }) : t('mvs_rest_sub_none') },
    quiet: { c: MV.quiet, Icon: Hourglass, title: t(room ? 'mvs_room_quiet' : 'mvs_quiet', { name }), sub: t('mvs_quiet_sub', { dur: durText(t, Math.max(1, Math.round((Date.now() - new Date(lastMotion || Date.now()).getTime()) / 60000))) }) },
    sensor: { c: MV.sensor, Icon: WifiSlash, title: t('mvs_sensor'), sub: t('mvs_sensor_sub', { name, when: rel(lastSignal) }) },
    nodata: { c: MV.sensor, Icon: Question, title: t('mvs_nodata'), sub: t('mvs_nodata_sub') },
  }[tone];
  const { c, Icon } = view;
  const size = compact ? 'w-8 h-8' : 'w-14 h-14';

  return (
    <div className="flex items-center gap-4 min-w-0" data-testid={testid} data-tone={tone}>
      <span className={`relative shrink-0 ${size} rounded-full grid place-items-center`}
        style={{ background: tone === 'sensor' || tone === 'nodata' ? HATCH : tone === 'rest' ? c.bg : c.dot, boxShadow: tone === 'rest' ? `inset 0 0 0 2px ${c.dot}` : 'none' }}>
        {tone === 'moving' && !compact && <span className="absolute inset-0 rounded-full animate-ping opacity-25" style={{ background: c.dot }} />}
        <Icon size={compact ? 16 : 26} weight={tone === 'rest' ? 'duotone' : 'fill'} color={tone === 'moving' || tone === 'quiet' ? '#FFFFFF' : c.text} className="relative" />
      </span>
      <div className="min-w-0">
        <div className={compact ? 'text-sm font-medium text-ink leading-snug' : 'font-heading text-xl sm:text-2xl text-ink leading-tight'} data-testid={`${testid}-title`}>{view.title}</div>
        <div className={`${compact ? 'text-[11px]' : 'text-sm mt-1'} leading-snug`} style={{ color: tone === 'quiet' ? MV.quiet.text : '#5C665E' }} data-testid={`${testid}-sub`}>{view.sub}</div>
      </div>
    </div>
  );
}
